/**
 * useItemObserver — curried ref factory used by codegen-emitted list hooks.
 *
 * NOT a public hook. Frontend code never imports this directly; it comes
 * through the list hook's return shape:
 *
 *   const { data, itemObserver } = useGetMarkets({ ..., viewportGated: true });
 *   data.map(row => <Card ref={itemObserver(row.pubkey)} data={row} />)
 *
 * Each `itemObserver(id)` returns a stable `RefCallback` for that id.
 * While the element is intersecting the viewport, the SDK registers the
 * row-scoped form of every subject template the hook declared in its
 * `realtime:` block — i.e. the `{rowKey}` placeholder segment
 * substituted with the row id. Literal `*` wildcard segments (e.g. the
 * `actor` segment) stay `*` on the wire — see `concretize`.
 *
 * The hook's declared subjects are the contract surface: subscribing to
 * channels the hook never declared (via `SUBJECTS_FOR[entityKey]`, which
 * fans out across every channel in `nats.manifest.yml#channels` for the
 * entity) would over-subscribe the wire AND mislead the devtool surface
 * about what events this hook actually consumes.
 *
 * The wildcard observers on the parent list hook (e.g. `morfi.market.*.
 * trade`) keep patching the cache; per-row registrations here are
 * visibility tracking the devtool surfaces.
 */
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useItemObserverResourceReader } from '../../devtool/hooks/resource-usage-reader-hooks.js';
import { RegistryContext } from '../context/RegistryContext.js';
const NOOP_EVENT = () => {};
const DETACHED_CACHE_CAP = 256;
const DETACHED_CACHE_TTL_MS = 5 * 60 * 1_000;
export function concretize(template, id, resolveField) {
  const segs = template.split('.');
  for (let i = 0; i < segs.length; i++) {
    const seg = segs[i];
    if (seg !== undefined && seg.length > 1 && seg.startsWith('{') && seg.endsWith('}')) {
      // `{field}` binds to the row's OWN field value when a resolver is present
      // (per-row `rowField:` binding — positions gate each row on its `market` /
      // `outcomeMint`); otherwise the placeholder IS the row id (the rowKey-bound
      // case). A field that resolves empty — a graduated position carries no
      // `market`, a bonding-curve one no `outcomeMint` — yields NO subject for
      // this template; return null so the caller skips it rather than putting a
      // malformed `morfi.market..user.*.trade.v0` on the wire.
      const field = seg.slice(1, -1);
      const value = resolveField !== undefined ? resolveField(id, field) : id;
      if (value === null || value.length === 0) return null;
      segs[i] = value;
    }
  }
  return segs.join('.');
}
/**
 * Returns a curried ref factory: `(id) => RefCallback<Element>`.
 *
 * The same `id` returns the same ref-callback across renders, so React
 * doesn't tear down + remount the IntersectionObserver registration on
 * every parent re-render. Calling with a fresh id allocates a new entry.
 *
 * `templates` are the per-row subject templates the hook declared in
 * `sdk.manifest.yml#queries.<name>.realtime` — the codegen-emitted
 * `rowSubjectTemplates`, which carry the row dimension as a `{rowKey}`
 * placeholder and keep literal `*` wildcard segments verbatim. Each is
 * concretized with the row id at attach time (placeholder only). Pass
 * `[]` (or omit per-entity realtime in the manifest) to get a no-op
 * ref factory.
 */
export function useItemObserver(templates, resolveField) {
  const registry = useContext(RegistryContext);
  const cacheStateRef = useRef(null);
  let cacheState = cacheStateRef.current;
  if (cacheState === null || cacheState.registryIdentity !== registry) {
    cacheState = createObserverCacheState(registry);
    cacheStateRef.current = cacheState;
  }
  const cache = useRef(cacheState.entries);
  cache.current = cacheState.entries;
  useItemObserverResourceReader(cache);
  useObserverCacheLifetime(cacheState);
  // Stable templates key + stable array — codegen typically returns a
  // module-level literal, but a fresh `.map(...)` from a wrapper would
  // break `useCallback` identity. Mirrors the `domainPinsStable` pattern
  // in `useRealtimeQuery.ts`.
  const templatesKey = templates.join('|');
  const templatesStable = useMemo(() => templatesKey.split('|').filter(s => s.length > 0), [templatesKey]);
  // The factory itself is stable across renders. We mutate `cache.current`
  // when new ids appear; React's commit phase calls the same ref-callback
  // for the same id, so no churn.
  return useCallback(id => {
    const existing = cache.current.get(id);
    if (existing !== undefined) {
      if (existing.el === null) {
        existing.detachedAt = Date.now();
        touchEntry(cacheState, id, existing);
      }
      return existing.ref;
    }
    // No registry or no templates → genuinely nothing to observe. We do NOT
    // gate on `hasVisibility()` here: that is a TRANSIENT condition (the
    // controller installs during `SDKProvider`'s render, after this render-
    // phase call, and a fresh post-flip registry re-arms async). Gating at
    // render time and caching the no-op latched a PERMANENT dead ref on the
    // first feed render whenever the controller wasn't ready yet, killing
    // realtime for the whole feed until a remount — the cold-mount bug this
    // hook now guards against. The `hasVisibility()` decision moves to
    // attach time (below), with an `onVisibilityInstalled` retry.
    if (templatesStable.length === 0 || registry === null) {
      // Return an uncached no-op: `registry === null` is transient (provider
      // not yet mounted). When it becomes non-null the registry-change reset
      // above mints a fresh cache, so a later render re-evaluates.
      return () => {};
    }
    const reg = registry;
    // A `{field}` template that can't resolve for this row (empty field) is
    // dropped — the row simply doesn't subscribe on that dimension.
    const subjects = templatesStable.map(t => concretize(t, id, resolveField)).filter(s_0 => s_0 !== null);
    // Synthetic queryKey used purely for reacquire telemetry — these
    // visibility-only registrations have no cache row to invalidate.
    const queryKey = ['__morfi_item_visibility__', id];
    const entry = {
      visRef: null,
      ref: () => {},
      el: null,
      unsubInstall: null,
      detachedAt: Date.now()
    };
    // `id` is threaded as `entityId` so the registry tracks this row's
    // viewport state — the conviction projector reads it (`isEntityVisible`)
    // to skip drift-projecting offscreen rows.
    const acquire = el => {
      entry.visRef = reg.acquireWhenVisible(subjects, NOOP_EVENT, queryKey, undefined, id);
      entry.visRef(el);
    };
    const refCb = el_0 => {
      if (cacheState.disposed) return;
      if (el_0 !== null && cache.current.get(id) !== entry) {
        cache.current.set(id, entry);
      }
      entry.el = el_0;
      entry.detachedAt = el_0 === null ? Date.now() : null;
      // Detach the element but retain this bounded registration so a rapid
      // virtualized-row reattach reuses its callback and visibility state.
      // Cache eviction owns the final `dispose()` call.
      if (entry.visRef !== null) {
        entry.visRef(el_0);
        if (el_0 !== null) return;
      }
      if (el_0 === null) {
        // Detached before the controller was ever ready — drop any pending
        // install wait so we don't acquire for a dead element.
        if (entry.unsubInstall !== null) {
          entry.unsubInstall();
          entry.unsubInstall = null;
        }
        if (cache.current.get(id) === entry) {
          touchEntry(cacheState, id, entry);
          schedulePrune(cacheState, 0);
        }
        return;
      }
      if (reg.hasVisibility()) {
        acquire(el_0);
        return;
      }
      // Controller not installed yet (cold mount before `SDKProvider`'s
      // install settled, or a just-swapped registry). Acquire the moment it
      // installs, for whatever element is attached then. One-shot.
      if (entry.unsubInstall === null) {
        entry.unsubInstall = reg.onVisibilityInstalled(() => {
          entry.unsubInstall = null;
          if (entry.el !== null && entry.visRef === null && reg.hasVisibility()) {
            acquire(entry.el);
          }
        });
      }
    };
    entry.ref = refCb;
    cache.current.set(id, entry);
    return refCb;
  }, [cacheState, registry, templatesStable, resolveField]);
}
function createObserverCacheState(registryIdentity) {
  return {
    registryIdentity,
    entries: new Map(),
    pruneTimer: null,
    pruneAt: null,
    disposeTimer: null,
    effectOwners: 0,
    disposed: false
  };
}
function useObserverCacheLifetime(state) {
  // Run after every committed render. `itemObserver(id)` is called during row
  // render, so the factory itself must never start timers: an abandoned
  // concurrent render has no effect phase and its provisional entries can be
  // garbage-collected with the abandoned hook state.
  useEffect(() => {
    schedulePrune(state, 0);
  });
  useEffect(() => {
    state.effectOwners += 1;
    if (state.disposeTimer !== null) {
      clearTimeout(state.disposeTimer);
      state.disposeTimer = null;
    }
    // Keep one low-frequency sweep armed even when the cache is currently
    // empty. A speculative child-row render can add an entry to this already
    // committed hook state without ever attaching its ref or re-rendering the
    // hook; the periodic sweep is what eventually collects that entry without
    // scheduling work from the speculative render itself.
    schedulePrune(state, DETACHED_CACHE_TTL_MS);
    return () => {
      state.effectOwners -= 1;
      state.disposeTimer = setTimeout(() => {
        state.disposeTimer = null;
        if (state.effectOwners === 0) disposeObserverCache(state);
      }, 0);
    };
  }, [state]);
}
function touchEntry(state, id, entry) {
  state.entries.delete(id);
  state.entries.set(id, entry);
}
function schedulePrune(state, delayMs) {
  if (state.disposed) return;
  const pruneAt = Date.now() + delayMs;
  if (state.pruneAt !== null && state.pruneAt <= pruneAt) return;
  if (state.pruneTimer !== null) clearTimeout(state.pruneTimer);
  state.pruneAt = pruneAt;
  state.pruneTimer = setTimeout(() => {
    state.pruneTimer = null;
    state.pruneAt = null;
    pruneDetachedEntries(state, Date.now());
  }, delayMs);
}
function pruneDetachedEntries(state, now) {
  let detachedCount = 0;
  for (const [id, entry] of state.entries) {
    if (entry.detachedAt === null) continue;
    if (now - entry.detachedAt >= DETACHED_CACHE_TTL_MS) {
      state.entries.delete(id);
      disposeCacheEntry(entry);
      continue;
    }
    detachedCount += 1;
  }
  if (detachedCount > DETACHED_CACHE_CAP) {
    for (const [id, entry] of state.entries) {
      if (detachedCount <= DETACHED_CACHE_CAP) break;
      if (entry.detachedAt === null) continue;
      state.entries.delete(id);
      disposeCacheEntry(entry);
      detachedCount -= 1;
    }
  }
  for (const entry of state.entries.values()) {
    if (entry.detachedAt === null) continue;
    schedulePrune(state, Math.max(0, entry.detachedAt + DETACHED_CACHE_TTL_MS - now));
    return;
  }
  if (state.effectOwners > 0) schedulePrune(state, DETACHED_CACHE_TTL_MS);
}
function disposeObserverCache(state) {
  if (state.disposed) return;
  state.disposed = true;
  if (state.pruneTimer !== null) clearTimeout(state.pruneTimer);
  state.pruneTimer = null;
  state.pruneAt = null;
  for (const entry of state.entries.values()) disposeCacheEntry(entry);
  state.entries.clear();
}
function disposeCacheEntry(entry) {
  if (entry.unsubInstall !== null) entry.unsubInstall();
  entry.unsubInstall = null;
  if (entry.visRef !== null) entry.visRef.dispose();
  entry.visRef = null;
  entry.el = null;
}